import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RichTextEditor } from "./RichTextEditor";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import { format } from "date-fns";
import { ImageUploader } from "./ImageUploader";
import { AIContentTools } from "./AIContentTools";
import { BlogPreview } from "./ContentPreview";

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string | null;
  content: string | null;
  featured_image_url: string | null;
  publish_date: string | null;
  seo_title: string | null;
  seo_description: string | null;
  is_published: boolean;
  created_at: string;
}

interface Tag {
  id: string;
  name: string;
  slug: string;
}

export const BlogManager = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const { toast } = useToast();

  // Form state
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [excerpt, setExcerpt] = useState("");
  const [content, setContent] = useState("");
  const [featuredImage, setFeaturedImage] = useState("");
  const [publishDate, setPublishDate] = useState("");
  const [seoTitle, setSeoTitle] = useState("");
  const [seoDescription, setSeoDescription] = useState("");
  const [isPublished, setIsPublished] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  useEffect(() => {
    fetchPosts();
    fetchTags();
  }, []);

  const fetchPosts = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("blog_posts")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      toast({ title: "Error loading posts", description: error.message, variant: "destructive" });
    } else {
      setPosts(data || []);
    }
    setIsLoading(false);
  };

  const fetchTags = async () => {
    const { data } = await supabase.from("tags").select("*").order("name");
    setTags(data || []);
  };

  const openDialog = async (post?: BlogPost) => {
    if (post) {
      setEditingPost(post);
      setTitle(post.title);
      setSlug(post.slug);
      setExcerpt(post.excerpt || "");
      setContent(post.content || "");
      setFeaturedImage(post.featured_image_url || "");
      setPublishDate(post.publish_date || "");
      setSeoTitle(post.seo_title || "");
      setSeoDescription(post.seo_description || "");
      setIsPublished(post.is_published);
      
      // Fetch post tags
      const { data: postTags } = await supabase
        .from("blog_post_tags")
        .select("tag_id")
        .eq("blog_post_id", post.id);
      setSelectedTags(postTags?.map((t) => t.tag_id) || []);
    } else {
      resetForm();
    }
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingPost(null);
    setTitle("");
    setSlug("");
    setExcerpt("");
    setContent("");
    setFeaturedImage("");
    setPublishDate("");
    setSeoTitle("");
    setSeoDescription("");
    setIsPublished(false);
    setSelectedTags([]);
  };

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  };

  const cleanJsonResponse = (result: string): string => {
    // Remove markdown code blocks if present
    let cleaned = result.replace(/```json\s*/gi, '').replace(/```\s*/g, '');
    // Try to extract JSON object or array from the string
    const jsonMatch = cleaned.match(/[\[{][\s\S]*[\]}]/);
    return jsonMatch ? jsonMatch[0].trim() : cleaned.trim();
  };

  const handleAIResult = (type: string, result: string) => {
    switch (type) {
      case "blog-draft":
        setContent((prev) => (prev ? prev + "\n\n" + result : result));
        break;
      case "seo-meta":
        try {
          const cleaned = cleanJsonResponse(result);
          const seoData = JSON.parse(cleaned);
          if (seoData.seoTitle) setSeoTitle(seoData.seoTitle);
          if (seoData.seoDescription) setSeoDescription(seoData.seoDescription);
        } catch (error) {
          console.error("Failed to parse SEO data:", result, error);
          toast({ title: "Could not parse SEO data", variant: "destructive" });
        }
        break;
      case "suggest-tags":
        try {
          const cleaned = cleanJsonResponse(result);
          const suggestedTags = JSON.parse(cleaned);
          const matchingTagIds = tags
            .filter((t) => suggestedTags.includes(t.name))
            .map((t) => t.id);
          setSelectedTags(matchingTagIds);
        } catch (error) {
          console.error("Failed to parse tags:", result, error);
          toast({ title: "Could not parse tags", variant: "destructive" });
        }
        break;
      case "generate-slug":
        setSlug(result.trim().replace(/['"]/g, ""));
        break;
    }
  };

  const savePost = async () => {
    if (!title || !slug) {
      toast({ title: "Title and slug are required", variant: "destructive" });
      return;
    }

    try {
      // Get current user for created_by tracking
      const { data: { user } } = await supabase.auth.getUser();

      const postData = {
        title,
        slug,
        excerpt: excerpt || null,
        content: content || null,
        featured_image_url: featuredImage || null,
        publish_date: publishDate || null,
        seo_title: seoTitle || null,
        seo_description: seoDescription || null,
        is_published: isPublished,
      };

      let postId = editingPost?.id;

      if (editingPost) {
        const { error } = await supabase
          .from("blog_posts")
          .update(postData)
          .eq("id", editingPost.id);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from("blog_posts")
          .insert({ ...postData, created_by: user?.id })
          .select()
          .single();
        if (error) throw error;
        postId = data.id;
      }

      // Update tags
      if (postId) {
        await supabase.from("blog_post_tags").delete().eq("blog_post_id", postId);
        if (selectedTags.length > 0) {
          await supabase.from("blog_post_tags").insert(
            selectedTags.map((tagId) => ({ blog_post_id: postId, tag_id: tagId }))
          );
        }
      }

      toast({ title: editingPost ? "Post updated!" : "Post created!" });
      setIsDialogOpen(false);
      fetchPosts();
    } catch (error: any) {
      toast({ title: "Error saving post", description: error.message, variant: "destructive" });
    }
  };

  const deletePost = async (id: string) => {
    if (!confirm("Delete this post?")) return;
    
    const { error } = await supabase.from("blog_posts").delete().eq("id", id);
    if (error) {
      toast({ title: "Error deleting post", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Post deleted" });
      fetchPosts();
    }
  };

  const toggleTag = (tagId: string) => {
    setSelectedTags((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId]
    );
  };

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold text-foreground">Blog Posts</h3>
        <Button onClick={() => openDialog()} size="sm">
          <Plus className="mr-2 h-4 w-4" />
          New Post
        </Button>
      </div>

      <div className="rounded-xl bg-card border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {posts.map((post) => (
              <TableRow key={post.id}>
                <TableCell className="font-medium">{post.title}</TableCell>
                <TableCell>
                  <Badge variant={post.is_published ? "default" : "secondary"}>
                    {post.is_published ? "Published" : "Draft"}
                  </Badge>
                </TableCell>
                <TableCell>
                  {post.publish_date
                    ? format(new Date(post.publish_date), "MMM d, yyyy")
                    : "—"}
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openDialog(post)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => deletePost(post.id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {posts.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                  No blog posts yet
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPost ? "Edit Post" : "New Post"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-6 pt-4">
            <AIContentTools
              title={title}
              description={excerpt}
              content={content}
              onResult={handleAIResult}
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                    if (!editingPost) setSlug(generateSlug(e.target.value));
                  }}
                  placeholder="Post title"
                />
              </div>
              <div>
                <Label htmlFor="slug">Slug</Label>
                <Input
                  id="slug"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  placeholder="post-url-slug"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="excerpt">Excerpt</Label>
              <Textarea
                id="excerpt"
                value={excerpt}
                onChange={(e) => setExcerpt(e.target.value)}
                placeholder="Brief description..."
                rows={2}
              />
            </div>

            <div>
              <Label>Content</Label>
              <RichTextEditor
                value={content}
                onChange={setContent}
                placeholder="Write your post content..."
              />
            </div>

            <div>
              <Label>Featured Image</Label>
              <ImageUploader
                bucket="blog-images"
                folder="featured"
                value={featuredImage}
                onChange={setFeaturedImage}
              />
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="publishDate">Publish Date</Label>
                <Input
                  id="publishDate"
                  type="date"
                  value={publishDate}
                  onChange={(e) => setPublishDate(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-4 pt-6">
                <Switch
                  id="published"
                  checked={isPublished}
                  onCheckedChange={setIsPublished}
                />
                <Label htmlFor="published">Published</Label>
              </div>
            </div>

            <div>
              <Label>Tags</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {tags.map((tag) => (
                  <Badge
                    key={tag.id}
                    variant={selectedTags.includes(tag.id) ? "default" : "outline"}
                    className="cursor-pointer"
                    onClick={() => toggleTag(tag.id)}
                  >
                    {tag.name}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="border-t pt-4">
              <h4 className="font-semibold mb-4">SEO Settings</h4>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="seoTitle">SEO Title</Label>
                  <Input
                    id="seoTitle"
                    value={seoTitle}
                    onChange={(e) => setSeoTitle(e.target.value)}
                    placeholder="SEO optimized title (60 chars max)"
                    maxLength={60}
                  />
                </div>
                <div>
                  <Label htmlFor="seoDescription">SEO Description</Label>
                  <Textarea
                    id="seoDescription"
                    value={seoDescription}
                    onChange={(e) => setSeoDescription(e.target.value)}
                    placeholder="Meta description (160 chars max)"
                    maxLength={160}
                    rows={2}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="secondary" onClick={() => setIsPreviewOpen(true)}>
                <Eye className="mr-2 h-4 w-4" />
                Preview
              </Button>
              <Button onClick={savePost}>
                {editingPost ? "Update Post" : "Create Post"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <BlogPreview
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        post={{
          title,
          excerpt,
          content,
          featuredImage,
          publishDate,
          tags: tags.filter((t) => selectedTags.includes(t.id)).map((t) => t.name),
        }}
      />
    </div>
  );
};
